import { IntegrationProvider } from "@repo/app-types/entities/integration";
import { SimpleModal, SimpleModalVariant } from "@repo/ui/Modal/modal";
import { useState, type FC } from "react";
import { useDisconnectGenesysIntegrationMutation } from "../../../api/entities/genesysIntegration.js";
import { getApiErrorMessage } from "../../../api/helpers/errors.js";
import { useGenesysDetailOutletContext } from "./genesysIntegrationDetailSection.js";
import { GenesysIntegrationSettings } from "./genesysIntegrationSettings.js";
import { integrationConfigs } from "./integrationConfigs.js";

const genesysConfig = integrationConfigs.find(
  (config) => config.provider === IntegrationProvider.GENESYS,
);

/**
 * The Genesys detail page's **Settings** tab: the existing connection UI
 * (connect / status / rotate / pause / disconnect), unchanged, plus the
 * disconnect confirmation it drives. Reads the shared integration off the
 * detail section's `Outlet` context.
 */
export const GenesysSettingsTab: FC = () => {
  const { integration, isLoading, isError, onReload } =
    useGenesysDetailOutletContext();
  const [disconnectGenesysIntegration] =
    useDisconnectGenesysIntegrationMutation();
  const [isDisconnectOpen, setIsDisconnectOpen] = useState(false);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);

  const handleDisconnect = async () => {
    if (!integration) {
      return;
    }
    setDisconnectError(null);
    try {
      await disconnectGenesysIntegration({
        integrationId: integration.id,
      }).unwrap();
    } catch (error) {
      setDisconnectError(
        getApiErrorMessage(
          error,
          "Could not disconnect Genesys Cloud — please try again.",
        ),
      );
    }
  };

  return (
    <div className="space-y-6">
      {/* The logo sits next to the name in the page header; the tab body leads
          with just the description, matching the other integration pages. */}
      <p className="max-w-3xl text-sm leading-6 text-muted-foreground">
        {genesysConfig?.description}
      </p>

      {/* A refused disconnect is the customer's to act on, so it is said out
          here rather than swallowed with the modal. */}
      {disconnectError ? (
        <p className="text-sm text-red-600">{disconnectError}</p>
      ) : null}

      <GenesysIntegrationSettings
        integration={integration}
        isLoading={isLoading}
        isError={isError}
        onReload={onReload}
        onRequestDisconnect={() => setIsDisconnectOpen(true)}
      />

      <SimpleModal
        isOpen={isDisconnectOpen}
        onCancel={() => setIsDisconnectOpen(false)}
        variant={SimpleModalVariant.Warning}
        title={`Disconnect ${genesysConfig?.name ?? "Genesys Cloud"}?`}
        subtitle={genesysConfig?.disconnectWarning}
        confirmButtonText="Disconnect"
        onConfirm={() => {
          void handleDisconnect();
          setIsDisconnectOpen(false);
        }}
      />
    </div>
  );
};
